import * as preact from "preact";
import * as _ from "underscore";

import models from "models/models";
import * as bookUtils from "shared/bookUtils";

interface Section {
  name?: string;
  start: number;
  selected?: boolean;
  subSelected?: boolean;
  children?: Section[];
}

interface Book {
  _id: string;
  title: string;
  url?: string;
  youTubeID?: string;
  sections?: Section[];
  hasSections?: boolean;
  urlExists?: boolean;
}

interface Props {
  readerViewModel: Backbone.Model;
  onSectionChange: (start: number) => void;
}

interface State {
  processedBook: Book;
}

class ReaderTitleView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      processedBook: this.processBookData(
        models.bookModel?.toJSON(),
        props.readerViewModel.get("fromPosition")
      ),
    };

    // Listen to model changes
    models.bookModel?.on("change:title change:sections", this.updateFromModels);
    props.readerViewModel.on("change:fromPosition", this.updateFromModels);
  }

  componentDidUpdate(prevProps: Props) {
    // Update event listeners if readerViewModel changes
    if (prevProps.readerViewModel !== this.props.readerViewModel) {
      prevProps.readerViewModel.off(
        "change:fromPosition",
        this.updateFromModels
      );
      this.props.readerViewModel.on(
        "change:fromPosition",
        this.updateFromModels
      );
      this.updateFromModels();
    }
  }

  componentWillUnmount() {
    // Clean up event listeners
    models.bookModel?.off(
      "change:title change:sections",
      this.updateFromModels
    );
    this.props.readerViewModel.off(
      "change:fromPosition",
      this.updateFromModels
    );
  }

  updateFromModels = () => {
    this.setState({
      processedBook: this.processBookData(
        models.bookModel?.toJSON(),
        this.props.readerViewModel.get("fromPosition")
      ),
    });
  };

  processBookData(book: Book | undefined, fromPosition: number): Book {
    if (!book) {
      return {
        _id: "",
        title: "",
      };
    }

    const processedBook = { ...book };

    // Deep copy book sections before altering them
    if (processedBook.sections) {
      processedBook.sections = JSON.parse(
        JSON.stringify(processedBook.sections)
      );
    }

    // Select the correct chapter
    if (processedBook.sections && processedBook.sections.length > 0) {
      let selectedSection:
        | { section: Section; type: "section" | "subSection" }
        | undefined;
      processedBook.hasSections = true;

      _.any(processedBook.sections, (section) => {
        if (section.start <= fromPosition) {
          if (section.name === bookUtils.dummyFirstSectionName) {
            // Hacky way to prevent showing the automatically added "Entire book" section title
            delete section.name;
          }
          selectedSection = {
            section: section,
            type: "section",
          };
          if (section.children) {
            return _.any(section.children, (subSection) => {
              if (subSection.start <= fromPosition) {
                selectedSection = {
                  section: subSection,
                  type: "subSection",
                };
                return false;
              }
              return true;
            });
          }
          return false;
        }
        return true;
      });

      if (selectedSection) {
        if (selectedSection.type === "section") {
          selectedSection.section.selected = true;
        } else {
          selectedSection.section.subSelected = true;
        }
      }
    }

    if (processedBook.url && processedBook.url.length > 0) {
      processedBook.urlExists = true;
    }

    return processedBook;
  }

  handleSectionChange = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const start = parseInt(
      target.selectedOptions[0].getAttribute("data-p") || "0",
      10
    );
    this.props.onSectionChange(start);
  };

  render() {
    const { processedBook } = this.state;

    // If book has URL, show link to original article
    if (processedBook.urlExists) {
      return (
        <a
          href={processedBook.url}
          title="Browse to original article"
          target="_blank"
        >
          <i class="icon-globe"></i> {processedBook.title}{" "}
          <i class="icon-long-arrow-right"></i>
        </a>
      );
    }

    // If book has YouTube ID, show YouTube link
    if (processedBook.youTubeID) {
      return (
        <a
          href={`http://www.youtube.com/watch?v=${processedBook.youTubeID}`}
          title="Open in YouTube"
        >
          {processedBook.title}
        </a>
      );
    }

    // If book has no sections, show simple title
    if (!processedBook.hasSections) {
      return <div class="titleText">{processedBook.title}</div>;
    }

    // If book has sections, show dropdown selector
    return (
      <div class="selectBox">
        <select class="sections" onChange={this.handleSectionChange}>
          <option data-p="0">{processedBook.title}</option>
          <option disabled>──────────</option>
          {processedBook.sections?.map((section) => (
            <>
              {section.name && (
                <option data-p={section.start} selected={section.selected}>
                  {section.name}
                </option>
              )}
              {section.children?.map((child) => (
                <option data-p={child.start} selected={child.subSelected}>
                  {child.name}
                </option>
              ))}
            </>
          ))}
        </select>
      </div>
    );
  }
}

export default ReaderTitleView;
